const hasCompleteCartPayload = (cart) =>
  cart &&
  typeof cart === 'object' &&
  typeof cart.item_count !== 'undefined' &&
  Array.isArray(cart.items);

const scheduleIdle = (callback, timeout = 900) => {
  if ('requestIdleCallback' in window) {
    return window.requestIdleCallback(callback, { timeout });
  }
  return window.setTimeout(callback, 240);
};

if (!customElements.get('ma-hawa-mobile-navigation')) {
  customElements.define(
    'ma-hawa-mobile-navigation',
    class MaHawaMinimalNavigation extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.root = this.querySelector('[data-ma-hawa-glass-root]');
        this.nav = this.querySelector('.ma-hawa-mobile-nav');
        this.count = this.querySelector('[data-bottom-cart-count]');
        this.viewCartLabel = this.dataset.viewCartLabel || '';

        this.handleNavIntent = (event) => this.onNavIntent(event);
        this.nav?.addEventListener('pointerdown', this.handleNavIntent);
        this.nav?.addEventListener('click', this.handleNavIntent);

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribeCart = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
            if (hasCompleteCartPayload(event?.cartData)) {
              this.update(event.cartData);
            } else {
              this.refreshCart();
            }
          });
        }

        scheduleIdle(() => {
          if (this.isConnected) this.refreshCart();
        });

        this.scrollToCheckoutFromHash();
      }

      disconnectedCallback() {
        this.nav?.removeEventListener('pointerdown', this.handleNavIntent);
        this.nav?.removeEventListener('click', this.handleNavIntent);
        if (this.unsubscribeCart) this.unsubscribeCart();
      }

      setActiveIndex(value) {
        const index = Math.max(0, Math.min(3, Number.parseInt(value, 10) || 0));
        if (this.root) this.root.dataset.activeIndex = String(index);
      }

      onNavIntent(event) {
        if (event.type === 'pointerdown' && event.button !== 0) return;
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

        const item = event.target.closest('[data-nav-index]');
        if (!item || !this.nav?.contains(item)) return;
        this.setActiveIndex(item.dataset.navIndex);
      }

      async refreshCart() {
        if (this.cartRequest) return this.cartRequest;

        this.cartRequest = (async () => {
          try {
            const cartUrl = window.routes?.cart_url || '/cart';
            const response = await fetch(`${cartUrl}.js`, {
              headers: { Accept: 'application/json' },
            });
            if (!response.ok) return;
            this.update(await response.json());
          } catch (error) {
            return;
          } finally {
            this.cartRequest = null;
          }
        })();

        return this.cartRequest;
      }

      update(cart) {
        const itemCount = Number(cart.item_count) || 0;
        this.updateCount(itemCount);

        this.getProgressContexts().forEach((context) => {
          const qualifyingCount = (cart.items || []).reduce((total, item) => {
            if (!context.productIds.has(String(item.product_id))) return total;
            return total + (Number(item.quantity) || 0);
          }, 0);

          const isReady = itemCount > 0 && qualifyingCount >= context.minimumOrder;
          context.progressBar.classList.toggle('is-checkout-ready', isReady);
          context.viewCartAction.hidden = !isReady;
        });
      }

      getProgressContexts() {
        return Array.from(document.querySelectorAll('ma-hawa-shop, ma-hawa-product-cart-progress'))
          .map((host) => {
            const progressBar = host.querySelector('[data-cart-progress], [data-product-cart-progress]');
            if (!progressBar) return null;

            let viewCartAction = progressBar.querySelector('[data-progress-checkout]');
            if (!viewCartAction) {
              viewCartAction = document.createElement('a');
              viewCartAction.className = 'ma-hawa-progress-checkout';
              viewCartAction.dataset.progressCheckout = '';
              viewCartAction.href = window.routes?.cart_url || '/cart';
              viewCartAction.textContent = this.viewCartLabel;
              viewCartAction.hidden = true;
              progressBar.appendChild(viewCartAction);
            }

            return {
              progressBar,
              viewCartAction,
              minimumOrder: Number(host.dataset.minimumOrder) || 5,
              productIds: new Set(
                (host.dataset.minimumProductIds || '')
                  .split(',')
                  .map((id) => id.trim())
                  .filter(Boolean),
              ),
            };
          })
          .filter(Boolean);
      }

      updateCount(value) {
        if (!this.count) return;
        const itemCount = Number(value) || 0;
        this.count.hidden = itemCount === 0;
        this.count.textContent = itemCount > 99 ? '99+' : String(itemCount);
      }

      scrollToCheckoutFromHash() {
        if (window.location.hash !== '#checkout') return;

        const scrollToTarget = () => {
          const checkout = document.getElementById('checkout');
          if (!checkout) return;
          const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          checkout.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'center' });
          if (typeof checkout.focus === 'function') checkout.focus({ preventScroll: true });
        };

        window.setTimeout(scrollToTarget, 350);
        window.addEventListener('load', () => window.setTimeout(scrollToTarget, 180), { once: true });
      }
    },
  );
}
